/* @flow */
import TickClass from 'lib/TickClass';

/**
 * Monitors mouse activity to determine whether the user is actively engaged
 */
class MouseActivity extends TickClass {
  /**
   * @param  {object} config - configuration object to set specific settings in the instance
   * @param {object} ...params - object of the rest of the parameters to easily pass to
   * super consturctor
   */
  constructor(config, ...params) {
    super(config, ...params);

    (this: any).setupEventListeners = this.setupEventListeners.bind(this);
    (this: any).resetIdle = this.resetIdle.bind(this);
    (this: any).tick = this.tick.bind(this);
    (this: any).releaseEventListeners = this.releaseEventListeners.bind(this);
    (this: any).browserMouseEvent = this.browserMouseEvent.bind(this);

    this._config = {
      tolerance: 10,
      ...this._config,
      ...config,
    };

    this._mouseActiveState = true;
    this._mouseIdleTime = 0;
    this._lastPosition = [0, 0];

    this.setupEventListeners();
  }

  /**
   * Setup event listeners that trigger active state changes
   * @private
   */
  setupEventListeners() {
    document.addEventListener('mousemove', this.browserMouseEvent);
    document.addEventListener('mousedown', this.browserMouseEvent);
    document.addEventListener('wheel', this.browserMouseEvent);
  }

  /**
   * Release event listeners that trigger active state changes
   * @private
   */
  releaseEventListeners() {
    document.removeEventListeners('mousemove', this.browserMouseEvent);
    document.removeEventListeners('mousedown', this.browserMouseEvent);
    document.addEventListener('wheel', this.browserMouseEvent);
  }

  /**
   * Increments the idle time every X milliseconds
   * @private
   */
  tick() {
    if (this._mouseActiveState) {
      this._mouseIdleTime += 1;

      if (this._mouseIdleTime >= this._config.tolerance) {
        this._mouseActiveState = false;
        this.dispatchEvent('statechange', false);
      }
    }
  }

  /**
   * Callback for mouse event listeners
   * @param  {string} event.type  Type of event
   * @param  {number} event.x     Horizontal coordinate of mouse
   * @param  {number} event.y     Vertical coordinate of mouse
   * @private
   */
  browserMouseEvent({ type, x, y }) {
    // Fix for IE11 because it triggers a mousemove event when the page content reflows
    if (type === 'mousemove') {
      if ((this._lastPosition[0] !== x) || (this._lastPosition[1] !== y)) {
        this._mouseIdleTime = 0;
      }
    } else {
      this._mouseIdleTime = 0;
    }

    this._lastPosition = [x, y];

    if (!this._mouseActiveState) {
      this._mouseActiveState = true;
      this.dispatchEvent('statechange', true);
    }
  }

  /**
   * Resets the idle to 0 or a specified number
   * @param  {Number} buffer - an optional number to set the idle to
   */
  resetIdle(buffer = 0) {
    this._mouseIdleTime = buffer;
  }

  /**
   * Get the current active state of the activity
   * @return {boolean}
   */
  get active() {
    return this._mouseActiveState;
  }

  /**
   * Cleanup any event listeners and variable references so it can be cleanly released
   * @param  {...Object} params - any unloading params that might be passed to super's
   * unload function
   */
  unload(...params) {
    this.releaseEventListeners();
    super.unload(...params);
  }
}

export default MouseActivity;
